import axios, { AxiosInstance } from 'axios'
import type {
  LoginResponse,
  SessionInfo,
  TokenInfo,
  TokenCreate,
  SystemOverview,
  StorageStats,
  AlertsResponse,
  ErrorsResponse,
  MetricsResponse,
  Insight,
  InsightStats,
  AiVerifyEstimate,
  AiVerifySummary,
  RequestLogEntry,
  PaginatedResponse,
  RegistrationResponse,
  FeedbackItem,
  FeedbackStats,
  FeedbackSubmitResponse,
  Correction,
  CorrectionSource,
  CorrectionVerdict,
  CorrectionCreateResult,
  CorrectionListResponse,
  CorrectionStats,
} from '@/types'

const BASE_URL = '/admin/api'

// Create axios instance
const axiosClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Include cookies
  headers: {
    'Content-Type': 'application/json',
  },
})

// Response interceptor for error handling
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login on auth error
      window.location.href = '/admin/login'
    }
    return Promise.reject(error)
  }
)

// API client
export const api = {
  auth: {
    login: async (apiToken: string): Promise<LoginResponse> => {
      const res = await axiosClient.post('/auth/login', { api_token: apiToken })
      return {
        success: res.data.success,
        permission: res.data.permission,
        tokenName: res.data.token_name,
        tokenId: res.data.token_id,
        expiresIn: res.data.expires_in,
      }
    },
    logout: async (): Promise<void> => {
      await axiosClient.post('/auth/logout')
    },
    getSession: async (): Promise<SessionInfo> => {
      const res = await axiosClient.get('/auth/session')
      return {
        permission: res.data.permission,
        tokenId: res.data.token_id,
        tokenName: res.data.token_name,
        createdAt: res.data.created_at,
        expiresAt: res.data.expires_at,
      }
    },
    getCsrf: async (): Promise<string> => {
      const res = await axiosClient.get('/auth/csrf')
      return res.data.token
    },
  },

  tokens: {
    list: async (): Promise<{ tokens: TokenInfo[]; total: number }> => {
      const res = await axiosClient.get('/tokens')
      return {
        tokens: res.data.tokens.map(mapToken),
        total: res.data.total,
      }
    },
    get: async (prefix: string): Promise<TokenInfo> => {
      const res = await axiosClient.get(`/tokens/${prefix}`)
      return mapToken(res.data)
    },
    getMy: async (): Promise<TokenInfo> => {
      const res = await axiosClient.get('/tokens/my')
      return mapToken(res.data)
    },
    create: async (data: TokenCreate): Promise<TokenInfo & { rawToken: string }> => {
      const res = await axiosClient.post('/tokens', snakeCaseKeys(data))
      return { ...mapToken(res.data), rawToken: res.data.raw_token }
    },
    update: async (prefix: string, data: Partial<TokenCreate>): Promise<TokenInfo> => {
      const res = await axiosClient.patch(`/tokens/${prefix}`, snakeCaseKeys(data))
      return mapToken(res.data)
    },
    delete: async (prefix: string): Promise<void> => {
      await axiosClient.delete(`/tokens/${prefix}`)
    },
    renew: async (prefix: string): Promise<{ newToken: string; newPrefix: string }> => {
      const res = await axiosClient.post(`/tokens/${prefix}/renew`)
      return { newToken: res.data.new_token, newPrefix: res.data.new_prefix }
    },
    clearStats: async (prefix: string): Promise<void> => {
      await axiosClient.post(`/tokens/${prefix}/clear-stats`)
    },
  },

  system: {
    getOverview: async (): Promise<SystemOverview> => {
      const res = await axiosClient.get('/system/overview')
      return mapSystemOverview(res.data)
    },
    getStorage: async (): Promise<StorageStats> => {
      const res = await axiosClient.get('/system/storage')
      return {
        totalDiskBytes: res.data.total_disk_bytes,
        totalDiskFormatted: res.data.total_disk_formatted,
        caches: res.data.caches.map((c: Record<string, unknown>) => ({
          name: c.name,
          diskSizeBytes: c.disk_size_bytes,
          diskSizeFormatted: c.disk_size_formatted,
          entries: c.entries,
          aliases: c.aliases || 0,
          hitRate: c.hit_rate,
          hits: c.hits,
          misses: c.misses,
          evictions: c.evictions || 0,
          memoryHits: c.memory_hits,
          memoryCacheSize: c.memory_cache_size,
          memoryCacheMax: c.memory_cache_max,
          memoryBytes: c.memory_bytes || 0,
          memoryBytesMax: c.memory_bytes_max || 0,
          memoryBytesFormatted: c.memory_bytes_formatted || '',
          memoryBytesMaxFormatted: c.memory_bytes_max_formatted || '',
          memoryOnly: c.memory_only || false,
        })),
      }
    },
    clearCache: async (resultCache: boolean, embeddingCache: boolean, requestCache: boolean = false): Promise<{ success: boolean; cachesCleared: string[]; errors: string[] }> => {
      const res = await axiosClient.post('/system/cache/clear', null, {
        params: { result_cache: resultCache, embedding_cache: embeddingCache, request_cache: requestCache },
      })
      return {
        success: res.data.success,
        cachesCleared: res.data.caches_cleared || [],
        errors: res.data.errors || [],
      }
    },
    getAlerts: async (): Promise<AlertsResponse> => {
      const res = await axiosClient.get('/system/alerts')
      return {
        active: res.data.active.map((a: Record<string, unknown>) => ({
          id: a.id,
          name: a.name,
          metric: a.metric,
          currentValue: a.current_value,
          threshold: a.threshold,
          triggeredAt: a.triggered_at,
        })),
        config: res.data.config.map((c: Record<string, unknown>) => ({
          id: c.id,
          name: c.name,
          metric: c.metric,
          comparison: c.comparison,
          threshold: c.threshold,
          isEnabled: c.is_enabled,
        })),
        history: res.data.history.map((h: Record<string, unknown>) => ({
          alertName: h.alert_name,
          value: h.value,
          message: h.message,
          triggeredAt: h.triggered_at,
        })),
      }
    },
    getErrors: async (page = 1, perPage = 50, errorType?: string, endpoint?: string): Promise<ErrorsResponse> => {
      const res = await axiosClient.get('/system/errors', {
        params: { page, per_page: perPage, error_type: errorType, endpoint },
      })
      return {
        errors: res.data.errors.map((e: Record<string, unknown>) => ({
          id: e.id,
          timestamp: e.timestamp,
          endpoint: e.endpoint,
          errorType: e.error_type,
          message: e.message,
          traceback: e.traceback,
        })),
        total: res.data.total,
        page: res.data.page,
        perPage: res.data.per_page,
        totalPages: res.data.total_pages,
        stats: {
          total: res.data.stats.total,
          today: res.data.stats.today,
          thisWeek: res.data.stats.this_week,
          errorTypes: res.data.stats.error_types,
          endpoints: res.data.stats.endpoints,
        },
      }
    },
    clearErrors: async (): Promise<void> => {
      await axiosClient.delete('/system/errors')
    },
    getMetrics: async (): Promise<MetricsResponse> => {
      const res = await axiosClient.get('/system/metrics')
      return {
        allTime: {
          totalRequests: res.data.all_time.total_requests,
          totalErrors: res.data.all_time.total_errors,
          totalResponseTimeMs: res.data.all_time.total_response_time_ms,
          avgResponseMs: res.data.all_time.avg_response_ms,
          maxResponseMs: res.data.all_time.max_response_ms,
          pairsProcessed: res.data.all_time.pairs_processed,
          pairsCompared: res.data.all_time.pairs_compared,
        },
        byEndpoint: res.data.by_endpoint.map((e: Record<string, unknown>) => ({
          endpoint: e.endpoint,
          requests: e.requests,
          errors: e.errors,
          avgResponseMs: e.avg_response_ms,
        })),
        byToken: res.data.by_token.map((t: Record<string, unknown>) => ({
          tokenPrefix: t.token_prefix,
          tokenName: t.token_name,
          totalRequests: t.total_requests,
          matchRequests: t.match_requests,
          matchEventsRequests: t.match_events_requests,
        })),
        decisionMethods: res.data.decision_methods.map((d: Record<string, unknown>) => ({
          method: d.method,
          count: d.count,
          percentage: d.percentage,
        })),
      }
    },
    getMaintenanceMode: async (): Promise<{ enabled: boolean }> => {
      const res = await axiosClient.get('/system/maintenance')
      return {
        enabled: res.data.enabled,
      }
    },
    setMaintenanceMode: async (enable: boolean): Promise<{ success: boolean; enabled: boolean }> => {
      const res = await axiosClient.post('/system/maintenance', null, {
        params: { enable },
      })
      return {
        success: res.data.success,
        enabled: res.data.enabled,
      }
    },
    getEmbeddingSyncStatus: async (): Promise<{ isRunning: boolean; progress: Record<string, unknown> | null }> => {
      const res = await axiosClient.get('/system/embedding-sync')
      return {
        isRunning: res.data.is_running,
        progress: res.data.progress,
      }
    },
    triggerEmbeddingSync: async (): Promise<{ success: boolean; alreadyRunning: boolean }> => {
      const res = await axiosClient.post('/system/embedding-sync')
      return {
        success: res.data.success,
        alreadyRunning: res.data.already_running,
      }
    },
  },

  insights: {
    list: async (params: {
      typeFilter?: 'fp' | 'fn'
      status?: 'pending' | 'rejected' | 'pending_deletion' | 'all'
      sport?: string
      page?: number
      perPage?: number
    }): Promise<PaginatedResponse<Insight>> => {
      const res = await axiosClient.get('/insights', {
        params: {
          type_filter: params.typeFilter,
          status: params.status,
          sport: params.sport,
          page: params.page,
          per_page: params.perPage,
        },
      })
      return {
        items: res.data.items.map(mapInsight),
        total: res.data.total,
        page: res.data.page,
        perPage: res.data.per_page,
        totalPages: res.data.total_pages,
      }
    },
    getStats: async (): Promise<InsightStats> => {
      const res = await axiosClient.get('/insights/stats')
      return {
        total: res.data.total,
        userReported: res.data.user_reported ?? 0,
        byType: res.data.by_type,
        byStatus: res.data.by_status,
        bySport: res.data.by_sport,
        aiUnchecked: res.data.ai_unchecked ?? 0,
      }
    },
    bulkAction: async (keys: string[], action: 'mark_rejected' | 'mark_deletion' | 'mark_pending' | 'delete'): Promise<void> => {
      await axiosClient.patch('/insights/bulk', { keys, action })
    },
    aiVerifyEstimate: async (): Promise<AiVerifyEstimate> => {
      const res = await axiosClient.get('/insights/ai-verify/estimate')
      return {
        count: res.data.count,
        estimatedCostUsd: res.data.estimated_cost_usd,
        model: res.data.model,
      }
    },
    aiVerify: async (
      onProgress: (progress: number, total: number) => void,
    ): Promise<AiVerifySummary> => {
      const res = await fetch(`${BASE_URL}/insights/ai-verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) {
        throw new Error(`AI verify failed: ${res.status}`)
      }
      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let summary: AiVerifySummary | null = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (data.done && data.summary) {
                summary = {
                  total: data.summary.total,
                  same: data.summary.same,
                  different: data.summary.different,
                  uncertain: data.summary.uncertain,
                  failed: data.summary.failed || 0,
                  markedRejected: data.summary.marked_rejected,
                }
              } else if (data.progress !== undefined) {
                onProgress(data.progress, data.total)
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }

      if (!summary) {
        throw new Error('AI verify stream ended without summary')
      }
      return summary
    },
  },

  requests: {
    list: async (params: {
      page?: number
      perPage?: number
      endpoint?: string
      scope?: 'mine'
    }): Promise<PaginatedResponse<RequestLogEntry>> => {
      const res = await axiosClient.get('/requests', {
        params: {
          page: params.page,
          per_page: params.perPage,
          endpoint: params.endpoint,
          scope: params.scope,
        },
      })
      return {
        items: res.data.logs.map(mapRequestLog),
        total: res.data.total,
        page: res.data.page,
        perPage: res.data.per_page,
        totalPages: res.data.total_pages,
      }
    },
    clear: async (): Promise<void> => {
      await axiosClient.delete('/requests')
    },
  },

  registration: {
    register: async (email?: string): Promise<RegistrationResponse> => {
      const res = await axiosClient.post('/register', { email: email || null })
      return {
        success: res.data.success,
        rawToken: res.data.raw_token,
        tokenPrefix: res.data.token_prefix,
        message: res.data.message,
      }
    },
  },

  feedback: {
    submit: async (body: {
      type: 'fp' | 'fn'
      match_mode: 'single' | 'event'
      name1?: string
      name2?: string
      sport: string
      notes?: string
      event1_left?: string
      event1_right?: string
      event2_left?: string
      event2_right?: string
    }): Promise<FeedbackSubmitResponse> => {
      const res = await axiosClient.post('/feedback', body)
      return {
        success: res.data.success,
        isNew: res.data.is_new,
        key: res.data.key,
        message: res.data.message,
      }
    },
    list: async (params: {
      typeFilter?: 'fp' | 'fn'
      status?: 'pending' | 'reviewed' | 'dismissed' | 'all'
      sport?: string
      page?: number
      perPage?: number
    }): Promise<PaginatedResponse<FeedbackItem>> => {
      const res = await axiosClient.get('/feedback', {
        params: {
          type_filter: params.typeFilter,
          status: params.status,
          sport: params.sport,
          page: params.page,
          per_page: params.perPage,
        },
      })
      return {
        items: res.data.items.map(mapFeedback),
        total: res.data.total,
        page: res.data.page,
        perPage: res.data.per_page,
        totalPages: res.data.total_pages,
      }
    },
    getStats: async (): Promise<FeedbackStats> => {
      const res = await axiosClient.get('/feedback/stats')
      return {
        total: res.data.total,
        byType: res.data.by_type,
        byStatus: res.data.by_status,
        bySport: res.data.by_sport,
      }
    },
    review: async (keys: string[], status: 'reviewed' | 'dismissed'): Promise<void> => {
      await axiosClient.patch('/feedback/review', { keys, status })
    },
    delete: async (keys: string[]): Promise<void> => {
      await axiosClient.delete('/feedback', { params: { keys: keys.join(',') } })
    },
  },

  corrections: {
    create: async (body: {
      source: CorrectionSource
      sourceKey?: string
      verdict: CorrectionVerdict
      sport?: string
      name1?: string
      name2?: string
    }): Promise<CorrectionCreateResult> => {
      const res = await axiosClient.post('/corrections', {
        source: body.source,
        source_key: body.sourceKey,
        verdict: body.verdict,
        sport: body.sport,
        name1: body.name1,
        name2: body.name2,
      })
      return {
        success: res.data.success,
        created: (res.data.created || []).map(mapCorrection),
        skipped: (res.data.skipped || []).map((s: Record<string, unknown>) => ({
          name1: s.name1 as string,
          name2: s.name2 as string,
          reason: s.reason as string,
        })),
      }
    },
    list: async (params: {
      sport?: string
      verdict?: CorrectionVerdict
      search?: string
      page?: number
      perPage?: number
    }): Promise<CorrectionListResponse> => {
      const res = await axiosClient.get('/corrections', {
        params: {
          sport: params.sport,
          verdict: params.verdict,
          search: params.search,
          page: params.page,
          per_page: params.perPage,
        },
      })
      return {
        items: (res.data.items || []).map(mapCorrection),
        total: res.data.total,
        page: res.data.page,
        perPage: res.data.per_page,
      }
    },
    getStats: async (): Promise<CorrectionStats> => {
      const res = await axiosClient.get('/corrections/stats')
      return {
        total: res.data.total,
        same: res.data.same,
        different: res.data.different,
      }
    },
    remove: async (id: number): Promise<void> => {
      await axiosClient.delete(`/corrections/${id}`)
    },
  },
}

// Helper functions for mapping API responses
function mapToken(data: Record<string, unknown>): TokenInfo {
  return {
    id: data.id as number,
    tokenPrefix: data.token_prefix as string,
    name: data.name as string | null,
    createdAt: data.created_at as number,
    lastUsedAt: data.last_used_at as number | null,
    expiresAt: data.expires_at as number | null,
    isActive: data.is_active as boolean,
    permission: data.permission as 'user' | 'reviewer' | 'admin',
    rateLimitPerSecond: data.rate_limit_per_second as number | null,
    maxBatchSize: data.max_batch_size as number | null,
    eventMaxSources: data.event_max_sources as number | null,
    eventMaxPerSource: data.event_max_per_source as number | null,
    totalRequests: data.total_requests as number,
    matchRequests: data.match_requests as number,
    matchEventsRequests: data.match_events_requests as number,
    pairsProcessed: data.pairs_processed as number,
    pairsCompared: data.pairs_compared as number,
    planId: (data.plan_id as string | null) ?? null,
    effectiveRateLimitPerSecond: (data.effective_rate_limit_per_second as number | null) ?? null,
    effectiveMaxBatchSize: (data.effective_max_batch_size as number | null) ?? null,
    effectiveEventMaxSources: (data.effective_event_max_sources as number | null) ?? null,
    effectiveEventMaxPerSource: (data.effective_event_max_per_source as number | null) ?? null,
  }
}

function mapSystemOverview(data: Record<string, unknown>): SystemOverview {
  const resources = data.resources as Record<string, unknown>
  const sessionMetrics = data.session_metrics as Record<string, unknown>
  const allTimeMetrics = data.all_time_metrics as Record<string, unknown>
  const recentMetrics = data.recent_metrics as Record<string, unknown>

  return {
    status: data.status as 'ok' | 'starting' | 'degraded' | 'error',
    uptimeSeconds: data.uptime_seconds as number,
    uptimeFormatted: data.uptime_formatted as string,
    errorRate: data.error_rate as number,
    insightsPending: data.insights_pending as number,
    resources: {
      cpuPercent: resources.cpu_percent as number,
      memoryPercent: resources.memory_percent as number,
      memoryUsedGb: resources.memory_used_gb as number,
      memoryTotalGb: resources.memory_total_gb as number,
      diskPercent: resources.disk_percent as number,
      diskFreeGb: resources.disk_free_gb as number,
    },
    sessionMetrics: {
      totalRequests: sessionMetrics.total_requests as number,
      totalErrors: sessionMetrics.total_errors as number,
      matchRequests: sessionMetrics.match_requests as number,
      matchEventsRequests: sessionMetrics.match_events_requests as number,
      pairsProcessed: sessionMetrics.pairs_processed as number,
      pairsCompared: sessionMetrics.pairs_compared as number,
      avgResponseMs: sessionMetrics.avg_response_ms as number,
    },
    allTimeMetrics: {
      totalRequests: allTimeMetrics.total_requests as number,
      totalErrors: allTimeMetrics.total_errors as number,
      matchRequests: allTimeMetrics.match_requests as number,
      matchEventsRequests: allTimeMetrics.match_events_requests as number,
      pairsProcessed: allTimeMetrics.pairs_processed as number,
      pairsCompared: allTimeMetrics.pairs_compared as number,
      avgResponseMs: allTimeMetrics.avg_response_ms as number,
    },
    recentMetrics: {
      requests60s: recentMetrics.requests_60s as number,
      requestsPerSecond: recentMetrics.requests_per_second as number,
      avgResponseMs60s: recentMetrics.avg_response_ms_60s as number,
      maxResponseMs60s: recentMetrics.max_response_ms_60s as number,
      errors60s: recentMetrics.errors_60s as number,
    },
  }
}

function mapInsight(data: Record<string, unknown>): Insight {
  const event1 = data.event1 as Record<string, unknown>
  const event2 = data.event2 as Record<string, unknown>

  return {
    key: data.key as string,
    type: data.type as 'fp' | 'fn',
    status: data.status as 'pending' | 'rejected' | 'pending_deletion',
    sport: data.sport as string,
    createdAt: parseTimestamp(data.created_at),
    event1: {
      source: event1.source as string,
      left: event1.left as string,
      right: event1.right as string,
      eventId: event1.event_id as string | undefined,
      timestamp: event1.timestamp as number | undefined,
    },
    event2: {
      source: event2.source as string,
      left: event2.left as string,
      right: event2.right as string,
      eventId: event2.event_id as string | undefined,
      timestamp: event2.timestamp as number | undefined,
    },
    similarity: data.similarity as number | undefined,
    clusterId: data.cluster_id as string | undefined,
    swapped: data.swapped as boolean,
    userReported: (data.user_reported as boolean) || false,
    userReportedBy: data.user_reported_by as string | undefined,
  }
}

function mapRequestLog(data: Record<string, unknown>): RequestLogEntry {
  return {
    id: data.id as string,
    timestamp: parseTimestamp(data.timestamp),
    endpoint: data.endpoint as string,
    method: data.method as string,
    requestBody: data.request_body as Record<string, unknown>,
    responseBody: data.response_body as Record<string, unknown>,
    statusCode: data.status_code as number,
    durationMs: data.duration_ms as number,
    clientIp: data.client_ip as string | undefined,
    error: data.error as string | undefined,
    apiKeyName: data.api_key_name as string | undefined,
    isAuthenticated: data.is_authenticated as boolean,
    fullyCached: data.fully_cached as boolean,
    originalProcessingMs: data.original_processing_ms as number | null | undefined,
  }
}

function mapFeedback(data: Record<string, unknown>): FeedbackItem {
  return {
    key: data.key as string,
    matchMode: (data.match_mode as 'single' | 'event') || 'single',
    feedbackType: data.feedback_type as 'fp' | 'fn',
    name1: data.name1 as string,
    name2: data.name2 as string,
    sport: data.sport as string,
    notes: data.notes as string | undefined,
    submittedBy: data.submitted_by as string,
    submittedAt: parseTimestamp(data.submitted_at),
    status: data.status as 'pending' | 'reviewed' | 'dismissed',
    reviewedBy: data.reviewed_by as string | undefined,
    reviewedAt: data.reviewed_at ? parseTimestamp(data.reviewed_at) : undefined,
    event1Left: data.event1_left as string | undefined,
    event1Right: data.event1_right as string | undefined,
    event2Left: data.event2_left as string | undefined,
    event2Right: data.event2_right as string | undefined,
  }
}

function mapCorrection(data: Record<string, unknown>): Correction {
  return {
    id: data.id as number,
    sport: data.sport as string,
    name1: data.name1 as string,
    name2: data.name2 as string,
    verdict: data.verdict as CorrectionVerdict,
    source: data.source as string,
    createdBy: (data.created_by as string | null) ?? null,
    createdAt: parseTimestamp(data.created_at),
  }
}

// Convert ISO string or number to Unix timestamp in seconds
function parseTimestamp(value: unknown): number {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const date = new Date(value)
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000)
    }
  }
  return 0
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function snakeCaseKeys<T extends object>(obj: T): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: Record<string, any> = {}
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
    result[snakeKey] = value
  }
  return result
}
